import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Pagination } from '@/components/ui/Pagination';
import { useAuth } from '@/hooks/useAuth';
import { PiApi } from '@/api';
import { formatMoneyWithSymbol, formatDate } from '@/lib/format';
import { Plus } from 'lucide-react';

export default function PiList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [managerId, setManagerId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const LIMIT = 25;

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => { setPage(1); }, [status, currency, debouncedSearch, dateRange, managerId]);

  const listQuery = useQuery({
    queryKey: ['piList', status, currency, debouncedSearch, dateRange, managerId, page],
    queryFn: () =>
      PiApi.list({
        ...(status && { status }),
        ...(currency && { currency }),
        ...(debouncedSearch && { q: debouncedSearch }),
        ...(dateRange && dateRange),
        ...(isAdmin && managerId && { created_by: managerId }),
        page,
        limit: LIMIT,
      }),
  });

  const pis = listQuery.data || { data: [], total: 0, page: 1, limit: LIMIT };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Proforma Invoices"
        subtitle="Manage all your PI documents"
        actions={
          <button className="btn btn-primary gap-2" onClick={() => navigate('/pis/new')}>
            <Plus size={16} /> New PI
          </button>
        }
      />

      {/* Filters */}
      <div className="card card-pad flex flex-wrap gap-4 items-end">
        <div className="w-full sm:flex-1 sm:min-w-56">
          <label className="label">Search</label>
          <input
            type="text"
            className="input mt-1"
            placeholder="PI #, customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Status</label>
          <select
            className="input mt-1"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <div>
          <label className="label">Currency</label>
          <select
            className="input mt-1"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="">All</option>
            <option value="INR">INR</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
        <div>
          <label className="label">From Date</label>
          <input
            type="date"
            className="input mt-1"
            value={dateRange?.from ?? ''}
            onChange={(e) =>
              setDateRange((prev) => ({
                from: e.target.value,
                to: prev?.to ?? new Date().toISOString().split('T')[0],
              }))
            }
          />
        </div>
        <div>
          <label className="label">To Date</label>
          <input
            type="date"
            className="input mt-1"
            value={dateRange?.to ?? ''}
            onChange={(e) => {
              const toVal = e.target.value;
              const fromVal = dateRange?.from ?? '';
              if (fromVal && toVal && toVal < fromVal) {
                setDateRange((prev) => ({ from: prev?.from ?? '', to: prev?.to ?? '' }));
              } else {
                setDateRange((prev) => ({ from: prev?.from ?? '', to: toVal }));
              }
            }}
          />
          {dateRange?.from && dateRange?.to && dateRange.to < dateRange.from && (
            <div className="text-xs text-rdx mt-1">"To" date cannot be before "From" date</div>
          )}
        </div>
        {isAdmin && (
          <div>
            <label className="label">Manager</label>
            <input
              type="text"
              className="input mt-1"
              placeholder="Manager ID..."
              value={managerId ?? ''}
              onChange={(e) => setManagerId(e.target.value || null)}
            />
          </div>
        )}
      </div>

      {/* Table */}
      {listQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : pis.data.length === 0 ? (
        <EmptyState
          title="No PIs found"
          body="Create a new proforma invoice to get started."
          action={
            <button className="btn btn-primary" onClick={() => navigate('/pis/new')}>
              Create PI
            </button>
          }
        />
      ) : (
        <div className="card flex flex-col md:max-h-[calc(100vh-280px)]">
          <div className="overflow-auto flex-1">
            <table className="table-clean">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>PI #</th>
                  <th>Customer</th>
                  <th>Currency</th>
                  <th>Grand Total</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {pis.data.map((pi: any) => (
                  <tr
                    key={pi.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/pis/${pi.id}`)}
                  >
                    <td className="font-medium text-rdx">{pi.pi_number}</td>
                    <td>{pi.customer_name}</td>
                    <td>{pi.currency}</td>
                    <td>{formatMoneyWithSymbol(pi.grand_total, pi.currency)}</td>
                    <td>
                      <StatusBadge status={pi.status} />
                    </td>
                    <td className="text-sm text-muted">{pi.created_by_name || pi.created_by}</td>
                    <td className="text-sm text-muted">{formatDate(pi.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-line px-4 shrink-0">
            <Pagination
              page={pis.page}
              total={pis.total}
              limit={pis.limit}
              onChange={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
